import React, { createContext, useContext } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

/**
 * Funnel-truth F2 (FT-AC-083..087, FT-AC-090/091) + F4 (FT-AC-092): ONE
 * page-level counting unit for every funnel metric on a surface — distinct
 * people or visits (journeys). The URL param is the ONLY carrier (D2: no
 * localStorage — a shared link reproduces the view exactly), and it carries
 * ONLY the non-default unit, so a fresh session renders DEFAULT_FUNNEL_UNIT.
 *
 * `parseFunnelUnitParam` and `applyFunnelUnitParam` are a symmetric PAIR kept
 * side by side on purpose: the writer omits whichever literal is the default,
 * so if either half hardcoded a unit instead of reading the constant, the
 * non-default choice would survive in React state and die on reload (F4).
 */
export type FunnelUnit = 'visits' | 'people';

/**
 * F4 flip (2026-08-27, FT-AC-092): people became the default unit — repeat
 * visits by one person collapse into one person. FT-AC-094 pinned `visits`
 * here for the whole F1–F3 validation window and is superseded by this flip.
 */
export const DEFAULT_FUNNEL_UNIT: FunnelUnit = 'people';

/** The URL search param carrying the unit (both surfaces). */
export const FUNNEL_UNIT_URL_PARAM = 'unit';

/**
 * D7 (FT-AC-087): the typed-absence line for a metric whose person field is
 * absent (old cached payload, fixture branch) — never 0, never the journey
 * number relabelled.
 */
export const PEOPLE_ABSENCE_LABEL = 'n/a · people data needs refresh';

/**
 * S9-B (people-attribution rework §16.2, AC-S9-9): the two typed absences of
 * the «Paid · people» tile. A «paid» label never carries the trial-inclusive
 * number and never a fabricated 0 — either the payload predates the paid
 * gate, or the warehouse has not materialized the paid clock for this scope.
 */
export const PAID_GATE_NOT_SERVED_DETAIL = 'paid gate not served by this payload';
export const PAID_TIMESTAMP_NOT_MATERIALIZED_DETAIL = 'paid timestamp not materialized yet';

/** D5 grammar: the unit noun is part of the visible group label, lowercase. */
export function unitNoun(unit: FunnelUnit): 'people' | 'visits' {
  return unit === 'people' ? 'people' : 'visits';
}

/**
 * Both literals are recognised; anything else (absent, empty, mis-cased, a
 * stale `unit=journeys`) falls back to the default. Symmetric with
 * `applyFunnelUnitParam` — see the file header.
 */
export function parseFunnelUnitParam(value: string | null | undefined): FunnelUnit {
  if (value === 'people') return 'people';
  if (value === 'visits') return 'visits';
  return DEFAULT_FUNNEL_UNIT;
}

/**
 * The other half of the pair: a link carries the unit only while it differs
 * from the default, so a copied default-view link stays clean. EVERY writer
 * goes through this or `funnelUnitQueryFragment` — a hand-written
 * `unit === 'people' ? '&unit=people' : ''` is correct only while people is
 * not the default, and silently sends the reader to the other unit once it is
 * (the F4 bug class: two such writers existed on the Open-in-canvas links).
 */
export function applyFunnelUnitParam(params: URLSearchParams, unit: FunnelUnit): void {
  if (unit === DEFAULT_FUNNEL_UNIT) params.delete(FUNNEL_UNIT_URL_PARAM);
  else params.set(FUNNEL_UNIT_URL_PARAM, unit);
}

/**
 * Same rule for links assembled as strings: returns `&unit=…` for a
 * non-default unit and an empty string for the default.
 */
export function funnelUnitQueryFragment(unit: FunnelUnit): string {
  return unit === DEFAULT_FUNNEL_UNIT ? '' : `&${FUNNEL_UNIT_URL_PARAM}=${unit}`;
}

const FunnelUnitContext = createContext<FunnelUnit>(DEFAULT_FUNNEL_UNIT);

/**
 * Context-for-DI (architecture §6): the canvas metric components live under
 * the reactFlow node-renderer tree — a provider at the surface root beats
 * drilling a `unit` prop through the canvas layer. Components outside any
 * provider read DEFAULT_FUNNEL_UNIT; today every consumer is inside one of the
 * two surface providers, so this fallback only shows up in unit tests.
 */
export const FunnelUnitProvider: React.FC<{ unit: FunnelUnit; children: React.ReactNode }> = ({
  unit,
  children,
}) => <FunnelUnitContext.Provider value={unit}>{children}</FunnelUnitContext.Provider>;

export function useFunnelUnit(): FunnelUnit {
  return useContext(FunnelUnitContext);
}

export interface FunnelUnitToggleProps {
  unit: FunnelUnit;
  onChange: (unit: FunnelUnit) => void;
  /** Surface-scoped testids (canvas vs Reports hub carry their own). */
  testIds: { root: string; option: (unit: FunnelUnit) => string };
}

/**
 * D1: compact exclusive segmented toggle «People | Visits» — two coequal
 * units, not an on/off switch. Shared by the canvas ReportModeBar (D3) and
 * the Reports scope bar (D4, instant — never Apply-gated).
 */
export const FunnelUnitToggle: React.FC<FunnelUnitToggleProps> = ({ unit, onChange, testIds }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={unit}
    aria-label="Counting unit"
    data-testid={testIds.root}
    onChange={(_event, next: FunnelUnit | null) => {
      // MUI reports null when the active option is clicked again — exclusive
      // toggles must never deselect into a unitless page.
      if (next === 'people' || next === 'visits') onChange(next);
    }}
  >
    <ToggleButton
      value="people"
      data-testid={testIds.option('people')}
      sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.8125rem' }}
    >
      People
    </ToggleButton>
    <ToggleButton
      value="visits"
      data-testid={testIds.option('visits')}
      sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.8125rem' }}
    >
      Visits
    </ToggleButton>
  </ToggleButtonGroup>
);
